import { useTranslation } from "react-i18next";
import type { PuzzleCredit } from "./api";

/** Only schemes a browser may safely follow; anything else is shown as plain text. */
function safeHref(url: string): string | null {
  return /^https?:\/\//i.test(url) ? url : null;
}

/** Hostname alone reads better than a full URL inline; the PDF sheet spells the address out. */
function linkLabel(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

/**
 * Attribution for a puzzle that comes from elsewhere: who made it, and where it lives.
 * Both halves are optional, so this renders nothing at all for an original puzzle.
 *
 * Never rendered before reveal — the link is playable off the clock (CLAUDE.md §10).
 */
export function PuzzleCreditLine({ credit }: { credit: PuzzleCredit | null }) {
  const { t } = useTranslation();
  if (!credit || (!credit.author && !credit.url)) return null;
  const href = credit.url ? safeHref(credit.url) : null;
  const label = credit.author || (credit.url ? linkLabel(credit.url) : "");
  return (
    <p className="bt-credit">
      <span className="bt-credit__label">{t("puzzles.source")}</span>{" "}
      {href ? (
        <a className="bt-credit__link" href={href} target="_blank" rel="noreferrer noopener">
          {label}
        </a>
      ) : (
        <span>{label}</span>
      )}
    </p>
  );
}
